import { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import type { PersonModelInterface, Payment } from "@/models/personModel";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
    CreditCard,
    Calendar,
    Percent,
    Timer,
    Wallet,
    TrendingUp,
    Coins,
    Plus,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import { getDebt } from "@/hooks/useClient";
import { calculateLoanDetails } from "@/utils/loanCalculation";
import LoanCalculationForms from "@/components/Forms/LoanCalculation/LoanCalculationForms";

function money(v?: number) {
    const n = Number(v || 0);
    return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n);
}

function dateRu(v?: any) {
    if (!v) return "Нет данных";
    const d = v?.toDate ? v.toDate() : v;
    const dt = dayjs(d);
    if (!dt.isValid()) return "Нет данных";
    return dt.format("DD.MM.YYYY");
}

function isClosed(status?: string) {
    return status === "closed" || status === "closedEarly" || status === "returned";
}

function StatusBadge({ status }: { status?: string }) {
    const map: Record<
        string,
        { label: string; className: string; icon?: any }
    > = {
        new: { label: "Новый", className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/20" },
        pending: { label: "В процессе", className: "bg-amber-500/15 text-amber-700 border-amber-500/20" },
        overdue: { label: "Просрочено", className: "bg-red-500/15 text-red-700 border-red-500/20", icon: AlertTriangle },
        closed: { label: "Закрыт", className: "bg-red-500/15 text-red-700 border-red-500/20", icon: CheckCircle2 },
        closedEarly: { label: "Закрыт досрочно", className: "bg-red-500/15 text-red-700 border-red-500/20", icon: CheckCircle2 },
        returned: { label: "Возврат", className: "bg-red-500/15 text-red-700 border-red-500/20" },
    };

    const c = map[status || ""] || {
        label: status || "Нет данных",
        className: "bg-muted text-muted-foreground",
    };
    const Icon = c.icon;

    return (
        <span className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${c.className}`}>
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
            {c.label}
    </span>
    );
}

function Stat({
                  icon: Icon,
                  label,
                  value,
                  hint,
              }: {
    icon: any;
    label: string;
    value: string;
    hint?: string;
}) {
    return (
        <div className="rounded-xl border bg-muted/20 p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="rounded-md border bg-background p-1.5">
                    <Icon className="h-4 w-4" />
                </div>
                <span>{label}</span>
            </div>
            <div className="mt-2 text-base font-semibold leading-tight">{value}</div>
            {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
        </div>
    );
}

type ChartRow = { month: string; expected: number; paid: number };

function buildScheduleChart(person: PersonModelInterface): ChartRow[] {
    const term = Number(person.creditDetail?.term || 0);
    const releaseDay = person.creditDetail?.releaseDay;
    const payments = (person.payments || []) as Payment[];

    const calc = calculateLoanDetails(
        Number(person.creditDetail?.phoneAmount || 0),
        Number(person.creditDetail?.downPayment || 0),
        term,
        Number(person.creditDetail?.percent || 0),
        String(releaseDay || ""),
        Number(person.phone?.costPrice || 0),
        payments,
        ["closed", "closedEarly", "returned"].includes(String(person.status || "")),
        null,
        String(person.status || ""),
        null
    );

    if (!term || !releaseDay) return [];

    const start = dayjs(releaseDay).locale("ru");
    if (!start.isValid()) return [];

    const rows: ChartRow[] = Array.from({ length: term }, (_, i) => {
        const monthDate = start.add(i + 1, "month");
        const paid = payments
            .filter((p) => p.type !== "discount")
            .filter((p) => {
                const pd = dayjs(p.date);
                return pd.isValid() && pd.month() === monthDate.month() && pd.year() === monthDate.year();
            })
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);

        return {
            month: monthDate.format("MMM YY"),
            expected: calc.monthlyPayment,
            paid,
        };
    });

    return rows;
}

export function CreditInfoCard({
                                   person,
                                   isEditing,
                                   onAddPayment,
                                   onEarlyClose,
                               }: {
    person: PersonModelInterface;
    isEditing: boolean;
    onAddPayment?: () => void;
    onEarlyClose?: () => void;
}) {
    const cd: any = person.creditDetail || {};
    const status = String(person.status || "");
    const closed = isClosed(status);

    const chartData = useMemo(() => buildScheduleChart(person), [person]);

    const phoneAmount = Number(cd.phoneAmount || 0);
    const downPayment = Number(cd.downPayment || 0);
    const term = Number(cd.term || 0);
    const percent = Number(cd.percent || 0);

    const payments = (person.payments || []) as Payment[];

    const calc = calculateLoanDetails(
        phoneAmount,
        downPayment,
        term,
        percent,
        String(cd.releaseDay || ""),
        Number(person.phone?.costPrice || 0),
        payments,
        ["closed", "closedEarly", "returned"].includes(status),
        null,
        status,
        null
    );

    const releaseDayText = dateRu(cd.releaseDay);
    const closingDayText = dateRu(cd.closingDay);

    const paymentsSorted = useMemo(() => {
        return [...payments].sort((a, b) => {
            const av = dayjs(a.date).valueOf();
            const bv = dayjs(b.date).valueOf();
            return bv - av;
        });
    }, [payments]);

    if (isEditing) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Информация о кредите
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <LoanCalculationForms showSchedule isDetailView />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Информация о кредите
                    </CardTitle>

                    <div className="flex items-center gap-2">
                        <StatusBadge status={status} />
                        {!closed && onAddPayment ? (
                            <Button size="sm" onClick={onAddPayment}>
                                <Plus className="h-4 w-4 mr-2" />
                                Добавить платеж
                            </Button>
                        ) : null}
                    </div>
                </div>
            </CardHeader>

            <CardContent className="space-y-5">
                <div className="grid gap-3 md:grid-cols-4">
                    <Stat icon={Wallet} label="Сумма телефона" value={`${money(phoneAmount)} сом`} />
                    <Stat icon={Coins} label="Первоначальный взнос" value={`${money(downPayment)} сом`} />
                    <Stat icon={Timer} label="Срок" value={`${term || 0} мес`} />
                    <Stat icon={Percent} label="Процент" value={`${percent || 0}%`} />
                </div>

                <Separator />

                <div className="grid gap-3 md:grid-cols-4">
                    <Stat icon={TrendingUp} label="Общая сумма" value={`${money(calc.total)} сом`} />
                    <Stat
                        icon={Coins}
                        label="Ежемесячный платеж"
                        value={`${money(closed ? 0 : calc.monthlyPayment)} сом`}
                        hint={closed ? "Кредит закрыт" : undefined}
                    />
                    <Stat
                        icon={Wallet}
                        label="Остаток долга"
                        value={`${money(getDebt(person))} сом`}
                        hint={closed ? "Кредит закрыт" : undefined}
                    />
                    <Stat
                        icon={TrendingUp}
                        label="Чистая прибыль"
                        value={`${money(calc.netProfit)} сом`}
                    />
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="rounded-md border bg-background p-1.5">
                                <Coins className="h-4 w-4" />
                            </div>
                            <span>Оплачено клиентом</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold">{money(calc.paidByClient)} сом</div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="rounded-md border bg-background p-1.5">
                                <Coins className="h-4 w-4" />
                            </div>
                            <span>Списано скидками</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold">{money(calc.discountApplied)} сом</div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="rounded-md border bg-background p-1.5">
                                <Coins className="h-4 w-4" />
                            </div>
                            <span>Погашено всего</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold">{money(calc.settledAmount)} сом</div>
                    </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="rounded-md border bg-background p-1.5">
                                <Calendar className="h-4 w-4" />
                            </div>
                            <span>Дата кредитования</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold">{releaseDayText}</div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="rounded-md border bg-background p-1.5">
                                <Calendar className="h-4 w-4" />
                            </div>
                            <span>Дата закрытия</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold">{closingDayText}</div>
                    </div>
                </div>

                {!closed && onEarlyClose ? (
                    <div className="flex justify-end">
                        <Button variant="outline" onClick={onEarlyClose}>
                            Закрыть досрочно
                        </Button>
                    </div>
                ) : null}

                <Separator />

                <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            График погашения
                        </div>
                        <Badge variant="outline">Оплачено: {money(calc.paidByClient)} сом</Badge>
                    </div>

                    <div className="h-[220px] rounded-xl border bg-background p-2">
                        {chartData.length === 0 ? (
                            <div className="h-full grid place-items-center text-sm text-muted-foreground">
                                Нет данных для графика
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="paidGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                    <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        className="text-xs fill-muted-foreground"
                                        tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "hsl(var(--popover))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: "var(--radius)",
                                            color: "hsl(var(--popover-foreground))",
                                        }}
                                        formatter={(value: any) => [`${money(Number(value))} сом`, ""]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="paid"
                                        stroke="hsl(var(--chart-1))"
                                        strokeWidth={2}
                                        fill="url(#paidGradient)"
                                        name="Выплачено"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <Separator />

                <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">История платежей</div>
                        <Badge variant="outline">Всего: {payments.length}</Badge>
                    </div>

                    {paymentsSorted.length === 0 ? (
                        <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
                            Платежей пока нет
                        </div>
                    ) : (
                        <div className="rounded-xl border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Дата</TableHead>
                                        <TableHead className="text-right">Сумма</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paymentsSorted.slice(0, 20).map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell>{dateRu(p.date)}</TableCell>
                                            <TableCell className="text-right font-medium">
                                                {p.type === "discount"
                                                    ? `Скидка ${money(Number(p.amount || 0))} сом`
                                                    : `${money(Number(p.amount || 0))} сом`}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {paymentsSorted.length > 20 ? (
                                <div className="px-4 py-2 text-xs text-muted-foreground">
                                    Показаны последние 20 платежей
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}